import * as React from 'react';

export type RetainedIndicatorView<View, Context> = {
	/** The view of the current query when it has arrived, otherwise the last one shown by this mount. */
	readonly view: View | null;
	/** The context that qualified `view` when it was fetched (the current one when `view` is current). */
	readonly context: Context | null;
	/** `true` while `view` is the previous answer and the current query is still in flight. */
	readonly isRefreshing: boolean;
};

type Retained<View, Context> = { readonly view: View; readonly context: Context | null };

/**
 * Keeps an indicator on screen while a filter change reloads it. The retained view lives in a ref
 * local to this mount, so a view left in the store by an earlier visit is never shown for another
 * key: a block that never displayed anything still gets its skeleton. `context` is whatever the
 * query key was built from and that qualifies the figure (a document type, for instance); it is
 * retained with the view so that text describing the figure never reads a newer filter.
 */
const useRetainedIndicatorView = <View extends { readonly queryKey: string }, Context = null>(
	data: View | null,
	queryKey: string | null,
	context?: Context,
): RetainedIndicatorView<View, Context> => {
	const retainedRef = React.useRef<Retained<View, Context> | null>(null);
	const current = data !== null && queryKey !== null && data.queryKey === queryKey ? data : null;
	const currentContext = context ?? null;

	React.useEffect(() => {
		if (current !== null) {
			retainedRef.current = { view: current, context: currentContext };
		}
	}, [current, currentContext]);

	return React.useMemo(() => {
		if (current !== null) {
			return { view: current, context: currentContext, isRefreshing: false };
		}

		const retained = retainedRef.current;

		return {
			view: retained?.view ?? null,
			context: retained?.context ?? null,
			isRefreshing: retained !== null,
		};
	}, [current, currentContext]);
};

export default useRetainedIndicatorView;
